<template>
  <section>
    <div
      class="relative mx-auto mt-12 mb-8 grid grid-cols-1 gap-6 md:gap-10 lg:max-w-none lg:grid-cols-5"
    >
      <LazyMarketingTabsProductsOverviewCopy :blok="blok" />
      <LazyMarketingTabsProductsOverviewLocationInfo
        v-if="isLocation(blok)"
        :blok="blok"
        :activities-count="activitiesCount"
        :gifts-count="giftsCount"
        :events-count="eventsCount"
        :packages-count="packagesCount"
        class="order-first lg:order-last"
      />
      <LazyMarketingTabsProductsOverviewActivityInfo
        v-if="isActivity(blok)"
        :blok="blok"
        :gifts-count="giftsCount"
        :events-count="eventsCount"
        :packages-count="packagesCount"
        class="order-first lg:order-last"
      />
      <LazyMarketingTabsProductsOverviewEventInfo
        v-if="isEvent(blok)"
        :blok="blok"
        class="order-first lg:order-last"
        @no-rsvp="emit('no-rsvp', $event)"
      />
      <LazyMarketingTabsProductsOverviewGroupInfo
        v-if="isGroup(blok)"
        :blok="blok"
        :activities-count="activitiesCount"
        class="order-first lg:order-last"
      />
    </div>
    <div v-if="blok.cta_banner && blok.cta_banner.length !== 0" class="mt-16">
      <UCard
        v-for="(banner, index) in blok.cta_banner"
        :key="index"
        :ui="{
          root: 'relative isolate w-full h-full bg-neutral-900/60 backdrop-blur-lg overflow-hidden',
        }"
      >
        <NuxtImg
          v-if="banner.content.feature_image.filename"
          provider="storyblok"
          :modifiers="{ smart: true }"
          sizes="xs:100svw sm:100svw md:100svw lg:100svw xl:100svw xxl:80svw"
          :src="banner.content.feature_image.filename"
          :alt="banner.content.feature_image.alt"
          class="absolute inset-0 h-full w-full object-cover object-center opacity-60"
          loading="lazy"
          placeholder
        />
        <div
          class="absolute inset-0 z-[1] h-full w-full bg-gradient-to-b from-transparent via-neutral-950/75 to-neutral-950"
        />
        <div
          class="relative z-[2] grid grid-cols-1 items-center gap-4 md:grid-cols-3"
        >
          <div class="col-span-2 w-full max-w-prose py-12">
            <div class="text-base font-extrabold text-yellow-400 uppercase">
              Addon
            </div>
            <h4 class="text-3xl font-extrabold">{{ banner.name }}</h4>
            <p class="mt-1 text-lg font-medium text-neutral-400">
              {{ banner.content.lead }}
            </p>
          </div>
          <UCard
            v-if="banner.full_slug.includes('addons')"
            :ui="{
              root: 'relative isolate bg-neutral-950/60 backdrop-blur-lg ring-yellow-400/50 overflow-hidden shadow-lg',
            }"
          >
            <div
              class="absolute inset-0 h-full w-full animate-pulse opacity-20"
              style="animation-duration: 5s"
            >
              <BrandTopography />
            </div>
            <div
              class="absolute inset-0 z-[1] h-full w-full bg-gradient-to-b from-transparent via-neutral-950/50 to-neutral-950"
            />

            <div class="relative z-[2] space-y-2 text-center">
              <div class="text-5xl font-extrabold">
                £{{ formatPrice(banner.content.price) }}
              </div>
              <UBadge class="font-extrabold" size="lg">
                Add At Checkout
              </UBadge>
            </div>
          </UCard>
        </div>
      </UCard>
    </div>
  </section>
</template>

<script setup lang="ts">
import type {
  LocationStoryblok,
  ActivityStoryblok,
  EventStoryblok,
  GroupStoryblok,
} from '~/types/component-types-sb.d.ts';

const emit = defineEmits<{
  (e: 'no-rsvp', value: boolean): void;
}>();

const { formatPrice } = useFormatPrice();

type Props = {
  blok: {
    location: LocationStoryblok;
    activity: ActivityStoryblok;
    event: EventStoryblok;
    group: GroupStoryblok;
  }[keyof {
    location: LocationStoryblok;
    activity: ActivityStoryblok;
    event: EventStoryblok;
    group: GroupStoryblok;
  }];
  type: 'location' | 'activity' | 'event' | 'group';
  activitiesCount: number;
  giftsCount: number;
  eventsCount: number;
  packagesCount: number;
};

defineProps<Props>();

const isLocation = (blok: Props['blok']): blok is LocationStoryblok =>
  blok.component === 'Location';
const isActivity = (blok: Props['blok']): blok is ActivityStoryblok =>
  // @ts-expect-error - Upper & lower case bloks in storyblok are different
  blok.component === 'Activity';
const isEvent = (blok: Props['blok']): blok is EventStoryblok =>
  blok.component === 'Event';
const isGroup = (blok: Props['blok']): blok is GroupStoryblok =>
  blok.component === 'Group';
</script>
